<template>
  <div class="volume-progress-chart relative mx-5 my-10">
    <template v-if="isOpen">
      <div class="l-flex-row volume-progress-chart__total">
        <span class="volume-progress-chart__label">{{ idTypeLabel }}</span>
        <spinner v-if="formulaVolumesLoading" width="20" height="20" />
        <Tooltip v-else :content="`${formatNumber(totalVolumeValue)}`" placement="top">
          <Tag type="info" size="small">
            <span style="color: black">{{ formatNumberIntl(totalVolumeValue) }}</span> {{ usersIdsLabelDisplay(idType) }}
          </Tag>
        </Tooltip>
      </div>
      <div class="progress-chart mb-4 flex h-5 overflow-hidden rounded bg-gray-100 text-xs">
        <template v-for="(volume, i) in currentVolumes">
          <template v-if="!formulaVolumesLoading">
            <div v-if="typeof volume.value === 'number'" :key="i" :style="{ width: `${volume._width}%`, background: chartColors[volume.key], position: 'relative' }" class="progress-chart--fragment" :class="{ 'no-border': checkBorder(volume._width) }">
              <Tooltip :content="`${volume.label} - ${volume.formatedValue}`" placement="top">
                <span class="legend-hidden-content">
                  {{ volume.label }} - {{ volume.formatedValue }}
                </span>
              </Tooltip>
            </div>
          </template>
        </template>
      </div>
    </template>
    <div class="volume-progress-chart__legend" :class="{ 'is-open': isOpen }">
      <div v-if="!isOpen" class="l-flex-row volume-progress-chart__legend-only">
        <spinner v-if="formulaVolumesLoading" width="30" height="30" />
        <Tooltip v-else :content="`${formatNumber(totalVolumeValueByIdType(activeIdType))}`" placement="top">
          <Tag type="info" size="small">
            <span style="color: black">{{ formatNumberIntl(totalVolumeValueByIdType(activeIdType)) }}</span> {{ usersIdsLabelDisplay(activeIdType) }}
          </Tag>
        </Tooltip>
      </div>
      <VolumeChartLegend :is-open="isOpen" :volumes="currentVolumes" :chart-colors="chartColors" :tooltip-placement="tooltipPlacement" />
    </div>
  </div>
</template>

<script>
import { orderVolumes, formatNumberIntl, formatNumber } from '@/utils/helpers'
import { mapState } from 'vuex'
import { Tooltip, Tag } from 'element-ui'
import VolumeChartLegend from './VolumeChartLegend.vue'

export default {
    props: {
        volumes: {
            default: () => ({}),
            type: Object
        },

        idType: {
            default: 'idbased',
            type: String
        },

        chartColors: {
            default: () => ({}),
            type: Object,
        },

        hideProgress: {
            default: false,
            type: Boolean,
        },

        tooltipPlacement: {
            default: 'bottom',
            type: String
        },

        isOpen: {
            default: true,
            type: Boolean,
        },
    },

    components: {
        Tooltip,
        Tag,
        VolumeChartLegend,
    },

    computed: {
        ...mapState('builder', {
            formulaVolumesLoading: state => state.formulaVolumesLoading,
            formulaVolumeType: state => state.formulaVolumeType,
        }),

        ...mapState('ui/settings', {
            firstPartyCookieSettings: state => state.general.firstPartyCookie,
            providerUtiqActivated: state => state.general.providerUtiqActivated,
            providerFirstIdActivated: state => state.general.providerFirstIdActivated,
        }),

        ...mapState('data/settings', {
            xdevice: state => state.general.xdevice,
        }),

        activeIdType() {
            return this.formulaVolumeType.toLowerCase() === 'id based' ? 'idbased' : 'xdevice'
        },

        idTypeLabel() {
            return this.idType === 'idbased' ? 'ID Based' : 'Cross Device'
        },

        totalVolumeValue() {
            return this.totalVolumeValueByIdType(this.idType)
        },

        totalVolumeValueByIdType() {
            return (idType) => {
                let total = 0
                if (idType === 'idbased') {
                    this.idBasedVolumes.forEach(({ value, key }) => {
                        if (key == 'total_doc') return
                        if (typeof this.volumes[key] === 'number') {
                            total += value
                        }
                    })
                } else {
                    this.xDeviceVolumes.forEach(({ value, key }) => {
                        if (key == 'total_users') return
                        total += value
                    })
                }

                return total
            }
        },

        // ...mapState('account', {
        //     namespaces: state => state.namespaces,
        // }),

        // accountHaveCRMNamespace() {
        //     return this.namespaces.some((namespace) => namespace.type === 'CRM')
        // },

        idBasedVolumes() {
            const idbasedVolumeData = {
                total_desktop: {
                    icon: 'cookie',
                    label: 'Cookie',
                },
                total_mobile: {
                    icon: 'mobile-volume',
                    label: 'Mobile',
                },
                total_crm: {
                    icon: 'crm-id',
                    label: 'CRM IDs',
                },
                total_desktop_first: {
                    icon: 'cookie-1st',
                    label: 'Cookie 1st',
                },
                total_firstid: {
                    icon: 'first-id-volume',
                    label: 'First-id',
                },
                total_utiq_mobile: {
                    icon: 'utiq',
                    label: 'Utiq Mobile',
                },
                total_utiq_home: {
                    icon: 'utiq',
                    label: 'Utiq Home',
                },
            }
            if (!this.volumes || !Object.keys(this.volumes).length) return []
            const _volumes = this.volumes
            const vOrder = orderVolumes('idbased')
            const $volumes = []
            vOrder.forEach((key) => {
                if (key === 'total_xdevice') return
                if (_volumes.hasOwnProperty(key) || ['total_utiq_home', 'total_utiq_mobile'].includes(key)) {
                    if (key === 'total_firstid' && !this.providerFirstIdActivated) return
                    if (key === 'total_desktop_first' && !this.firstPartyCookieSettings) return
                    if (key === 'total_utiq_home' && !this.providerUtiqActivated) return
                    if (key === 'total_utiq_mobile' && !this.providerUtiqActivated) return
                    // if (key === 'total_crm' && !this.accountHaveCRMNamespace) return
                    if (!_volumes[key]) {
                        _volumes[key] = 0
                    }
                    const tmpValue = _volumes[key]
                    $volumes.push({
                        key,
                        value: tmpValue,
                        formatedValue: formatNumber(tmpValue),
                        displayValue: formatNumberIntl(tmpValue),
                        ...idbasedVolumeData[key]
                    })
                }
            })
            return [ ...$volumes ]
        },

        xDeviceVolumes() {
            if (this.volumes?.total_xdevice ) {
                const xDeviceVolumeData = {
                    total_users_desktop: {
                        icon: 'desktop-volume',
                        label: 'Web',
                    },
                    total_users_desktop_first: {
                        icon: 'desktop-first',
                        label: 'Web 1st',
                    },
                    total_users_crm: {
                        icon: 'crm-id',
                        label: 'CRM IDs',
                    },
                    total_users_mobile: {
                        icon: 'app-volume',
                        label: 'App',
                    },
                    total_users_firstid: {
                        icon: 'first-id-volume',
                        label: 'First-id',
                    },
                    total_users_utiq_mobile: {
                        icon: 'utiq',
                        label: 'Utiq Mobile',
                    },
                    total_users_utiq_home: {
                        icon: 'utiq',
                        label: 'Utiq Home',
                    },
                }
                const _volumes = this.volumes.total_xdevice
                const vOrder = orderVolumes('xdevice')

                const $volumes = []
                vOrder.forEach((key) => {
                    if (key === 'total_users') return
                    if (_volumes.hasOwnProperty(key) || ['total_users_utiq_home', 'total_users_utiq_mobile'].includes(key)) {
                        if (key === 'total_users_firstid' && !this.providerFirstIdActivated) return
                        if (key === 'total_users_desktop_first' && !this.firstPartyCookieSettings) return
                        if (key === 'total_users_utiq_home' && !this.providerUtiqActivated) return
                        if (key === 'total_users_utiq_mobile' && !this.providerUtiqActivated) return
                        // if (key === 'total_users_crm' && !this.accountHaveCRMNamespace) return
                        if (!_volumes[key]) {
                            _volumes[key] = 0
                        }
                        const tmpValue = _volumes[key]
                        $volumes.push({
                            key,
                            value: tmpValue,
                            formatedValue: formatNumber(tmpValue),
                            displayValue: formatNumberIntl(tmpValue),
                            ...xDeviceVolumeData[key]
                        })
                    }
                })
                return [ ...$volumes ]
            }
            return []
        },

        currentVolumes() {
            if (!this.isOpen) {
                if (!this.xdevice) {
                    return this.idBasedVolumes
                }
                return this.activeIdType === 'idbased' ? this.idBasedVolumes : this.xDeviceVolumes
            }

            const volumes = this.idType === 'idbased' ? this.idBasedVolumes : this.xDeviceVolumes
            const calcPrc = (value, base = 100 /* 100% */) => parseFloat((value / this.totalVolumeValue) * base)
            let lessFivePer = 0
            const basePer = 1.5 // 1.5%

            const positiveVolumes = volumes.some((volume) => volume.value > 0)

            if (!positiveVolumes) {
                volumes.map(volume => {
                    volume._width = parseFloat(100 / volumes.length).toFixed(2)
                    return volume
                })
                return volumes
            }

            volumes.map(volume => {
                const per = calcPrc(volume.value)
                if (per === 0) {
                    volume._width = 0
                } else {
                    volume._width = per > basePer ? per : null
                    if (per < basePer) {
                        lessFivePer++
                    }
                }
                return volume
            })
            volumes.map(volume => {
                const per = calcPrc(volume.value, 100 - lessFivePer * basePer)
                volume._width = volume._width === null ? basePer : per
                return volumes
            })

            return volumes
        },
    },

    methods: {
        formatNumber,
        formatNumberIntl,
        volumePourcentage(volume) {
            return parseFloat((volume / this.totalVolumeValue) * 100).toFixed(2)
        },

        iconSize(volume) {
            const medium = ['total_users_mobile', 'total_users_firstid', 'total_firstid']
            if (medium.includes(volume.key)) return 'medium'
            return 'large'
        },

        checkBorder(volume) {
            return parseInt(volume) === 0
        },

        usersIdsLabelDisplay(type) {
            return type === 'idbased' ? 'IDs' : 'Users'
        }
    }
}
</script>

<style lang="scss">
@keyframes skeleton-loading {
  to {
    background-position: left;
  }
}
.volume-progress-chart {
  padding: 20px 0;

  &__legend {
    display: flex;
    justify-content: start;
    align-items: center;
  }
  &__label {
    color: #257FFE;
    margin-right: 10px;
    white-space: nowrap;
  }
  &__total {
    width: 100%;
    height: 24px;
    justify-content: start;
    align-items: center;
    font-size: 1rem;
  }
  &__legend-only {
    margin-right: 40px;
    width: 50px;
    height: 30px;
    align-items: center;
  }
}
.legend-hidden-content {
    position: absolute;
    left: 0;
    top: 0;
    font-size: 0;
    height: 10px;
    width: 100%
}
.progress-chart {
    margin: 20px 0;
    display: flex;
    overflow: hidden;
    height: 10px;
    border-radius: 4px;
    background: gray;
    min-width: 500px;
    content: ' ';
    cursor: progress;
    background: linear-gradient(90deg, #ddda 40%, #efefefaa, #ddda 60%) right / 300% 100%;
    animation: skeleton-loading 1.5s linear infinite;
    &--fragment {
        border-left: 1px solid white;
        &.no-border, &:first-child {
            border-left: unset;
        }
    }
}
</style>
